// Лонч: имя и даты. Что записать — planLaunchSave (новый получает первую дорожку и трек); удаление уносит всё каскадом.
import { useCanWrite } from '@core/auth/access';
import { useState, type FormEvent } from 'react';
import { Modal, TabPanels } from '@core/ui/Modal';
import { SegmentedControl } from '@core/ui/SegmentedControl';
import { ObjectHistory } from '../history/ObjectHistory';
import { DeleteConfirm, FormError, ModalFooter, fieldInput as input, fieldLabel as label, readOnlyFields, submitOnCtrlEnter } from '@core/ui/form';
import { data, useTable } from '@core/store';
import { planLaunchSave, type LaunchDraft } from './planner';

const FORM_ID = 'launch-form';

export function LaunchModal({ draft, onClose, onSaved }: { draft: LaunchDraft; onClose: () => void; onSaved?: (id: string) => void }) {
  const canWrite = useCanWrite();
  const launches = useTable('launch');
  const [d, setD] = useState(draft);
  const [tab, setTab] = useState<'main' | 'history'>('main');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(e: FormEvent) {
    e.preventDefault();
    const plan = planLaunchSave(d, launches.values(), () => crypto.randomUUID());
    if ('error' in plan) { setError(plan.error); return; }
    if (plan.changes.length === 0) { onClose(); return; }
    setBusy(true); setError(null);
    const r = await data.applyChanges(plan.changes, { kind: d.id ? 'edit_launch' : 'create_launch', launch_id: plan.launchId });
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    onSaved?.(plan.launchId);
    onClose();
  }
  async function remove() {
    if (!d.id) return;
    setBusy(true);
    const r = await data.applyChanges([{ table: 'launch', id: d.id, delete: true }], { kind: 'delete_launch', launch_id: d.id });
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    onClose();
  }
  const footer = (
    <ModalFooter form={FORM_ID} busy={busy} onCancel={onClose} readOnly={!canWrite} left={d.id && <DeleteConfirm label="Delete launch" question={<>Delete «{d.name}» with all its rows and tasks? They stay in the history.</>} busy={busy} onDelete={() => void remove()} />} />
  );
  const form = (
    <form id={FORM_ID} onSubmit={(e) => void save(e)} onKeyDown={canWrite ? submitOnCtrlEnter : undefined} className="flex flex-col gap-4">
      <label className={label}>Name
        <input className={input} value={d.name} autoFocus required onChange={(e) => setD({ ...d, name: e.target.value })} />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className={label}>From
          <input type="date" className={input} value={d.start_date} required onChange={(e) => setD({ ...d, start_date: e.target.value })} />
        </label>
        <label className={label}>To
          <input type="date" className={input} value={d.end_date} min={d.start_date} required onChange={(e) => setD({ ...d, end_date: e.target.value })} />
        </label>
      </div>
      <FormError error={error} />
    </form>
  );
  const head = d.id ? <SegmentedControl role="tabs" label="Launch card" className="mb-4" value={tab} onChange={setTab} items={[{ key: 'main', label: 'Launch' }, { key: 'history', label: 'History' }]} /> : undefined;
  return (
    <Modal open onClose={onClose} title={d.id ? 'Launch' : 'New launch'} head={head} footer={footer}>
      {d.id ? <TabPanels active={tab} panels={[['main', readOnlyFields(!canWrite, form)], ['history', <ObjectHistory table="launch" id={d.id} active={tab === 'history'} />]]} /> : readOnlyFields(!canWrite, form)}
    </Modal>
  );
}
